import { memo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import LanguageSwitcher from "@components/LanguageSwitcher/LanguageSwitcher";
import styles from "./Header.module.css";

/**
 * Public header logo: brand link to landing page
 */
const PublicLogo = memo(({ tagline, homeLabel }) => (
  <Link to="/" className={styles.logoContainer} aria-label={homeLabel}>
    <div className={styles.logo} aria-hidden="true">
      <span className={styles.logoTextPublic}>AVIAFY</span>
    </div>
    {tagline && (
      <div className={styles.logoDescription}>
        <span>{tagline}</span>
      </div>
    )}
  </Link>
));

PublicLogo.displayName = "PublicLogo";
PublicLogo.propTypes = { tagline: PropTypes.string };

/**
 * Auth navigation links for public header
 */
const AuthButtons = memo(({ loginText, registrationText, navLabel }) => (
  <nav className={styles.authButtonsContainer} aria-label={navLabel}>
    <LanguageSwitcher type="landing" />
    <Link
      to="/login"
      className="px-4 py-2 text-sm font-medium text-white hover:text-blue-400 transition-colors"
    >
      {loginText}
    </Link>
    <Link
      to="/registration"
      className="ml-2 px-5 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-all shadow-sm active:transform active:scale-95"
    >
      {registrationText}
    </Link>
  </nav>
));

AuthButtons.displayName = "AuthButtons";
AuthButtons.propTypes = {
  loginText: PropTypes.string.isRequired,
  registrationText: PropTypes.string.isRequired,
};

/**
 * PublicHeader: Used on landing, login, and registration pages.
 * Features a dark theme with high contrast for better visibility.
 */
const PublicHeader = ({ staffAuth = false }) => {
  const { t } = useTranslation();
  const tagline = t("createAndManage");

  return (
    <header
      className={`${styles.header} ${styles.bgDarkbg} ${staffAuth ? styles.staffAuth : ""}`}
      role="banner"
    >
      <div className="max-w-[1440px] w-full mx-auto flex items-center justify-between">
        <PublicLogo tagline={tagline} homeLabel={t("nav.home")} />

        <div className={styles.userInputContainer}>
          <AuthButtons
            navLabel={t("authFlow.navigation")}
            loginText={t("login")}
            registrationText={t("registration")}
          />
        </div>
      </div>
    </header>
  );
};

PublicHeader.displayName = "PublicHeader";
export default memo(PublicHeader);
